(ns hai.tasks.assignee-picker
  "Popover-based searchable picker for reassigning a task, opened by clicking
   its assignee avatar. Searches the current user's personal contacts that have
   an email (`hai.tasks.data/::search-contacts` filters out the rest) — picking
   one sends `:contact/uuid` to the `task/assign` mutation, which ensures
   (finds-or-creates, by email) the corresponding Humble user and assigns the
   task to them.

   Modeled on `hai.notes.contact-combobox` / `hai.tags.combobox`: typing runs a
   debounced, server-side `:contact/search` query (`::search-contacts` stores
   the results in `::data/contacts`, shared by every row's picker on the page).
   The dropdown is empty until you type, since `:contact/search` returns nothing
   for a blank query.

   Pass `:on-pick` to take over what a pick does — the task form uses it to
   stage the contact until Save instead of assigning on the spot."
  (:require
    [clojure.string :as str]
    [hai.contacts.helpers :as contacts]
    [hai.tasks.data :as data]
    [hai.ui.components :as c]
    [hai.ui.icons :as icons]
    [hai.ui.rf-sugar :refer [<s >e]]
    [hai.ui.tokens :as t]
    [lambdaisland.ornament :as o]
    [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))

(def ^:private debounce-ms 300)

;; Unstyled-button reset around whatever avatar is passed in as `trigger` — only
;; adds a click affordance (pointer + hover/focus ring), the avatar itself keeps
;; its existing look. `.pill` is for wide triggers (the form's assignee chip),
;; where a 50% radius would draw the ring as an ellipse.
(o/defstyled picker-trigger :button
  :inline-flex :cursor-pointer
  {:border "none" :background "transparent" :padding "0" :border-radius "50%"
   :transition "box-shadow 0.15s ease"}
  [:&.pill {:border-radius "999px"}]
  [:&:hover {:box-shadow "0 0 0 2px var(t/--card-sage-soft-20)"}]
  [:&:focus-visible {:outline "none" :box-shadow "0 0 0 2px var(t/--card-sage)"}])

(o/defstyled search-input :input
  {:width "100%" :box-sizing "border-box"
   :padding "6px 10px" :margin-bottom "4px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :border-radius "6px" :outline "none"
   :font-family "inherit" :font-size "0.8125rem"
   :color t/--card-forest}
  [:&:focus {:border-color t/--card-sage}]
  [:&::placeholder {:color t/--card-forest-70}])

;; Separate styled component so its CSS still applies once the Popover portals
;; the list out of the trigger's DOM subtree — same trick as `contact-results`.
(o/defstyled results :div
  :flex :flex-col :overflow-y-auto
  {:min-width "220px" :max-height "220px"}
  [:.row :cursor-pointer :flex :items-center
   {:gap "8px" :padding "8px 10px" :font-size "0.8125rem" :border-radius "6px"}
   [:&:hover {:background t/--card-forest-06}]]
  [:.row-icon {:display "inline-flex" :color t/--card-forest-70}
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]]
  [:.empty {:padding "8px 10px" :font-size "0.8125rem" :color t/--card-forest-70}]
  ([{:keys [entries empty-label]}]
   (cond
     (seq entries)
     (into [:<>]
           (map (fn [{:keys [key label pick]}]
                  ^{:key key}
                  [:div.row {:on-click pick}
                   [:span.row-icon [person-icon]]
                   [:span label]]))
           entries)
     ;; No `empty-label` (blank search) → render nothing rather than a
     ;; misleading "no matches" for a box the user hasn't typed into yet.
     empty-label [:div.empty empty-label])))

(o/defstyled popover-body :div
  {:padding "8px"}
  ([{:keys [on-pick !open? !text on-search]}]
   (let [contacts (or @(<s [::data/contacts]) [])
         query    @(<s [::data/contacts-query])
         text     @!text
         entries  (mapv (fn [contact]
                          {:key   (:contact/uuid contact)
                           :label (contacts/->label+email contact)
                           :pick  (fn [_]
                                    (on-pick contact)
                                    (reset! !open? false)
                                    (reset! !text ""))})
                        contacts)]
     [:<>
      [search-input
       {:type        "text"
        :value       text
        :placeholder "Search contacts…"
        :aria-label  "Search contacts"
        :auto-focus  true
        :on-change   (fn [^js e]
                       (let [v (.. e -target -value)]
                         (reset! !text v)
                         (on-search v)))}]
      ;; Only "No matching contacts" once the completed search matches what's in
      ;; the box — while a keystroke's debounced query is still in flight, `query`
      ;; lags `text`, so we stay silent instead of flashing the empty message.
      [results {:entries     entries
                :empty-label (when (and (not (str/blank? text)) (= query text))
                               "No matching contacts")}]])))

(defn component
  "`trigger` is the already-rendered avatar hiccup (e.g. `[assignee-avatar
   assignee]`). When `editable?` is false it's rendered as-is, unwrapped — a
   read-only row gets the plain, non-interactive avatar it always had.

   By default a pick assigns `task-uuid` straight away. `:on-pick` overrides
   that and receives the picked contact instead, leaving `task-uuid` unused —
   that's the mode the task form uses, since the form applies its assignee on
   Save rather than on click. `:trigger-class` and `:label` style/name the
   trigger button."
  [{:keys [task-uuid editable? trigger trigger-class label on-pick]}]
  (if-not editable?
    trigger
    (r/with-let [!open?    (r/atom false)
                 !text     (r/atom "")
                 !timer    (atom nil)
                 search!   (fn [text]
                             (if (str/blank? text)
                               (>e [::data/clear-contacts])
                               (>e [::data/search-contacts text])))
                 schedule! (fn [text]
                             (when-let [t @!timer] (js/clearTimeout t))
                             (reset! !timer (js/setTimeout #(search! text) debounce-ms)))]
      ;; Read from props on every render rather than freezing them in the
      ;; `with-let` bindings above, which only run at mount.
      (let [pick! (or on-pick
                      (fn [contact]
                        (>e [::data/assign task-uuid
                             {:contact/uuid (:contact/uuid contact)}])))]
        [c/PopoverRoot
         {:open         @!open?
          :onOpenChange (fn [open?]
                          (reset! !open? open?)
                          (when-not open?
                            (reset! !text "")
                            (>e [::data/clear-contacts])))}
         [c/PopoverTrigger
          [:div [picker-trigger {:type       "button"
                                 :class      trigger-class
                                 :aria-label (or label "Change assignee")}
                 trigger]]]
         [c/PopoverContent {:side "bottom" :align "start" :sideOffset 4}
          [popover-body {:on-pick pick! :!open? !open? :!text !text :on-search schedule!}]]])
      (finally
        (when-let [t @!timer] (js/clearTimeout t))))))
